import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  TextField,
  Skeleton,
  InputAdornment,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
  Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import IssueDetailDialog from "./IssueDetailDialog";
import MOCK_RCA_RESPONSE from "./mockData";

const COLORS = {
  pageBg: "#eef1f8",
  cardBg: "#ffffff",
  border: "#e3e6ef",
  navy: "#123072",
  navyDark: "#2a5cad",
  textPrimary: "#1c2333",
  textSecondary: "#6b7280",
  textMuted: "#6b7896",
  open: "#f5c842",
  collab: "#5ac8f6",
  closed: "#4ade80",
  escalation: "#f87171",
  e1Bg: "#eef0f5",
  e1Text: "#6b7280",
  e2Bg: "#fdecc8",
  e2Text: "#a5690a",
  e3Bg: "#fbdada",
  e3Text: "#c62828",
  textResponsible: "#9c174f",
  bgResponsible: "#fde7f3",
  textUpd: "#1557a0",
  bgUpd: "#e3f2fd",
};

const chipSx = {
  borderRadius: "2px",
};

const STAGE_META = {
  open: { label: "Open", color: COLORS.open, sub: "Raised — no responsible-person comment yet" },
  collaboration: { label: "Collaboration", color: COLORS.collab, sub: "Responsible person has commented — still open" },
  closed: { label: "Closed", color: COLORS.closed, sub: "Closed by the Account Owner" },
};

const STAGE_ORDER = ["open", "collaboration", "closed"];

const ESCALATION_STYLES = {
  E1: { bg: COLORS.e1Bg, color: COLORS.e1Text },
  E2: { bg: COLORS.e2Bg, color: COLORS.e2Text },
  E3: { bg: COLORS.e3Bg, color: COLORS.e3Text },
  E4: { bg: COLORS.e3Bg, color: COLORS.e3Text },
};

const SIDEBAR_WIDTH = 300;

const DEFAULT_FILTERS = {
  month: [],
  state: [],
  region: [],
  district: [],
  escalation: [],
  plant: [],
};

const ALL_PLANTS = { label: "All Plants", value: "" };

const UNASSIGNED_PLANT = "Unassigned";
const UNKNOWN_STATE = "Unknown State";
const UNKNOWN_DISTRICT = "Unknown District";

const slug = (str) =>
  String(str || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-") || "unknown";

// ---------------------------------------------------------------------------
// Loading skeletons
// ---------------------------------------------------------------------------

const SummaryBannerSkeleton = () => (
  <Box sx={{ mb: 1.5 }}>
    <Skeleton variant="rounded" height={40} sx={{ borderRadius: "10px" }} />
  </Box>
);

const IssueCardSkeleton = () => (
  <Box
    sx={{
      border: `1px solid ${COLORS.border}`,
      borderRadius: "10px",
      p: 1.5,
      mb: 1.25,
    }}
  >
    <Stack direction="row" justifyContent="space-between">
      <Skeleton width={90} height={20} />
      <Skeleton variant="rounded" width={40} height={18} />
    </Stack>

    <Skeleton width="60%" height={18} sx={{ mt: 1 }} />
    <Skeleton width="95%" height={18} />
    <Skeleton width="80%" height={18} />

    <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
      <Skeleton width={100} height={18} />
      <Skeleton width={70} height={18} />
    </Stack>

    <Skeleton width={120} height={18} />
  </Box>
);

const DashboardSkeleton = ({ isCompact }) => (
  <>
    <SummaryBannerSkeleton />
    <Box
      sx={{
        display: "flex",
        flexDirection: isCompact ? "column" : "row",
        gap: 2,
        minHeight: isCompact ? "auto" : 420,
      }}
    >
      {!isCompact && (
        <Skeleton variant="rounded" width={SIDEBAR_WIDTH} height={420} sx={{ borderRadius: "12px", flexShrink: 0 }} />
      )}
      <Box
        sx={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr 1fr",
          gap: 2,
        }}
      >
        {(isCompact ? [0] : [0, 1, 2]).map((col) => (
          <Box key={col} sx={{ bgcolor: "#f4f6f9", borderRadius: "12px", p: 1.5 }}>
            <Skeleton width="50%" height={22} sx={{ mb: 1 }} />
            <IssueCardSkeleton />
            <IssueCardSkeleton />
          </Box>
        ))}
      </Box>
    </Box>
  </>
);

// ---------------------------------------------------------------------------
// Filters / keyword search / summary banner
// ---------------------------------------------------------------------------

const FilterDropdown = ({
  label,
  value,
  options,
  onChange,
  disableClearable = false,
  getOptionLabel,
  isOptionEqualToValue,
  disabled,
  width = 200,
  compactWidth,
  isCompact,
}) => (
  <Autocomplete
    disabled={disabled}
    size="small"
    options={options}
    value={value}
    onChange={(event, newValue) => onChange(newValue)}
    disableClearable={disableClearable}
    getOptionLabel={getOptionLabel || ((opt) => (typeof opt === "string" ? opt : opt?.label ?? ""))}
    isOptionEqualToValue={
      isOptionEqualToValue ||
      ((option, selected) => {
        if (typeof option === "string" || typeof selected === "string") {
          return option === selected;
        }
        return option?.value === selected?.value;
      })
    }
    sx={{
      width: isCompact ? compactWidth ?? "150px" : width,
      flexShrink: 0,
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        variant="outlined"
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 6,
            fontSize: 12,
            height: 35,
            paddingRight: "20px!important",
          },
        }}
      />
    )}
  />
);

const KeywordSearch = ({ value, onChange, isCompact }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent={isCompact ? "stretch" : "flex-end"}
    sx={{
      mt: isCompact ? 0 : 0.5,
      mb: isCompact ? 1.5 : 1.5,
      position: "relative",
      zIndex: 2,
      pointerEvents: "none",
    }}
  >
    <TextField
      size="small"
      placeholder="Search any keyword"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 18, color: COLORS.textMuted }} />
          </InputAdornment>
        ),
      }}
      sx={{
        width: isCompact ? "100%" : 300,
        maxWidth: isCompact ? "100%" : 300,
        marginLeft: "0px!important",
        pointerEvents: "auto",
        "& .MuiOutlinedInput-root": {
          borderRadius: 6,
          fontSize: 12,
          height: 35,
          bgcolor: COLORS.cardBg,
        },
      }}
    />
  </Box>
);

const SummaryBanner = ({ stateCount, totals }) => {
  const metrics = [
    {
      key: "states",
      value: stateCount,
      label: stateCount === 1 ? "STATE" : "STATES",
      color: "rgba(255,255,255,0.92)",
    },
    { key: "open", value: totals.open, label: "Open", color: COLORS.open },
    { key: "collab", value: totals.collaboration, label: "Collab", color: COLORS.collab },
    { key: "closed", value: totals.closed, label: "Closed", color: COLORS.closed },
  ];

  return (
    <Box
      sx={{
        borderRadius: "10px",
        px: 1,
        py: 0.65,
        mb: 0,
        background: `
          linear-gradient(
            135deg,
            #2C5CC5 0%,
            #3E73E8 45%,
            #5A97FF 100%
          )
        `,
        boxShadow: "0 4px 12px rgba(32, 84, 194, 0.14)",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        divider={
          <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "rgba(255,255,255,0.28)", my: 0.25 }} />
        }
      >
        {metrics.map((m) => (
          <Stack
            key={m.key}
            direction="row"
            spacing={0.75}
            alignItems="baseline"
            justifyContent="center"
            sx={{ flex: 1, minWidth: 0, px: { xs: 0.5, sm: 1 } }}
          >
            <Typography
              sx={{
                fontSize: m.key === "states" ? { xs: 12, sm: 13 } : { xs: 15, sm: 18 },
                fontWeight: 800,
                lineHeight: 1,
                color: m.color,
                letterSpacing: m.key === "states" ? "0.03em" : 0,
                textShadow: m.key === "states" ? "none" : "0 1px 2px rgba(0,0,0,0.25)",
              }}
            >
              {m.value}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 10, sm: 11 },
                fontWeight: m.key === "states" ? 700 : 500,
                color: m.key === "states" ? "rgba(255,255,255,0.92)" : "#F5FAFF",
                letterSpacing: m.key === "states" ? "0.03em" : 0,
              }}
            >
              {m.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Board (issue cards) — unchanged visuals
// ---------------------------------------------------------------------------

const DESCRIPTION_LINE_CLAMP = 2;

const descriptionTextSx = {
  fontSize: 12.5,
  fontWeight: 500,
  color: COLORS.textPrimary,
  lineHeight: 1.4,
  letterSpacing: "-0.005em",
  textAlign: "left",
};

const descriptionToggleSx = {
  color: "#2f6fd1",
  fontWeight: 500,
  cursor: "pointer",
  whiteSpace: "nowrap",
  "&:hover": { textDecoration: "underline" },
};

const IssueCard = ({ issue, stage, onClick, isRefreshing = false }) => {
  const [titleExpanded, setTitleExpanded] = useState(false);
  const [titleOverflows, setTitleOverflows] = useState(false);
  const descriptionRef = useRef(null);
  const fullTitle = issue?.title || "";
  
  useEffect(() => {
    setTitleExpanded(false);
  }, [fullTitle]);

  useEffect(() => {
    if (isRefreshing) return;

    const el = descriptionRef.current;
    if (!el || !fullTitle || titleExpanded) return;

    const checkOverflow = () => {
      setTitleOverflows(el.scrollHeight > el.clientHeight + 1);
    };

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [fullTitle, titleExpanded, isRefreshing]);

  if (isRefreshing) {
    return <IssueCardSkeleton />;
  }

  const meta = STAGE_META[stage];
  const esc = ESCALATION_STYLES[issue.escalation] || ESCALATION_STYLES.E1;
  const dealerName = issue.dealerName || "";

  const site = issue.site === "-" ? "Unavailable" : issue.site;
  const locationName = dealerName || "Unknown dealer";
  const locationId = issue.dealer ? `#${issue.dealer}` : "";
  const locationTitle = [locationName, locationId].filter(Boolean).join(" ");

  return (
    <Box
      onClick={() => onClick?.(issue)}
      className="logistics-rca-issue-card"
      sx={{
        bgcolor: COLORS.cardBg,
        border: `1px solid ${COLORS.border}`,
        borderLeft: `3px solid ${meta.color}`,
        borderRadius: "8px",
        px: 1.5,
        py: 1.25,
        mb: 1.25,
        cursor: "pointer",
        fontFamily: '"Inter", sans-serif',
        textAlign: "left",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          boxShadow: "0 2px 10px rgba(28,35,51,0.07)",
          borderColor: COLORS.border,
          borderLeftColor: meta.color,
        },
        // Beat leaked text-align:center from other modules (TSTT/Communications tables, etc.)
        "& .MuiTypography-root": { textAlign: "left" },
      }}
    >
      {/* Line 1: Plant + escalation */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ gap: 1, mb: 0.45 }}
      >
        <Typography
          noWrap
          title={site}
          sx={{
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: site === "Unavailable" ? COLORS.textMuted : COLORS.navy,
            minWidth: 0,
            flex: 1,
          }}
        >
          {site}
        </Typography>
        <Chip
          label={issue.escalation}
          size="small"
          sx={{
            ...chipSx,
            height: 18,
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: "0.02em",
            bgcolor: esc.bg,
            color: esc.color,
            flexShrink: 0,
          }}
        />
      </Stack>

      {/* Line 2: Assignee | A/O  + date on the right */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ gap: 1, mb: 0.45, flexWrap: "wrap", rowGap: 0.35 }}
      >
        <Stack
          direction="row"
          spacing={0.65}
          alignItems="center"
          sx={{ minWidth: 0, flex: 1 }}
        >
          <PersonOutlineIcon
            sx={{ fontSize: 14, color: COLORS.textMuted, flexShrink: 0 }}
          />
          <Typography
            noWrap
            title={issue.owner}
            // sx={{
            //   fontSize: 12,
            //   fontWeight: 600,
            //   color: issue.owner === "Unassigned" ? COLORS.textMuted : "#1a2b4a",
            //   minWidth: 0,
            // }}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: issue.owner === "Unassigned" ? COLORS.textMuted : COLORS.textResponsible,
              backgroundColor: issue.owner === "Unassigned" ? "transparent" : COLORS.bgResponsible,
              borderRadius: "4px",
              px: 0.75,
              py: 0.15,
              minWidth: 0,
              maxWidth: 120,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {issue.owner}
          </Typography>

          <Box
            sx={{
              width: "1px",
              height: 12,
              bgcolor: "#d0d5e0",
              flexShrink: 0,
            }}
          />

          <Typography
            noWrap
            title={issue.updatedBy || "—"}
            // sx={{
            //   fontSize: 12,
            //   fontWeight: 600,
            //   color: issue.updatedBy ? "#1a2b4a" : COLORS.textMuted,
            //   minWidth: 0,
            // }}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: issue.updatedBy ? COLORS.textUpd : COLORS.textMuted,
              backgroundColor: issue.updatedBy ? COLORS.bgUpd : "transparent",
              borderRadius: "4px",
              px: 0.75,
              py: 0.15,
              minWidth: 0,
              maxWidth: 120,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {issue.updatedBy || "—"}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          spacing={0.4}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          <CalendarTodayOutlinedIcon sx={{ fontSize: 12, color: COLORS.textMuted }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 400, color: COLORS.textMuted }}>
            {issue.date || "—"}
          </Typography>
        </Stack>
      </Stack>

      {/* Line 2.5: MTD Actual vs MTD Target */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 0.45 }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: COLORS.textSecondary,
          }}
        >
          MTD Actual:
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: COLORS.navy,
          }}
        >
          {issue.actual ?? "—"}
        </Typography>

        <Box
          sx={{
            width: "1px",
            height: 12,
            bgcolor: "#d0d5e0",
          }}
        />

        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: COLORS.textSecondary,
          }}
        >
          MTD Target:
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: COLORS.navy,
          }}
        >
          {issue.mtd_target ?? "—"}
        </Typography>
      </Stack>

      {/* Line 3: Dealer name (ellipsis) + #id immediately after */}
      <Stack
        direction="row"
        spacing={0.6}
        alignItems="center"
        sx={{ minWidth: 0, mb: 0.85 }}
        title={locationTitle}
      >
        <StorefrontOutlinedIcon
          sx={{ fontSize: 14, color: COLORS.textMuted, flexShrink: 0 }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            component="span"
            noWrap
            sx={{
              fontSize: 12,
              fontWeight: 400,
              color: COLORS.textSecondary,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {locationName}
          </Typography>
          {locationId && (
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 400,
                color: COLORS.textMuted,
                flexShrink: 0,
                ml: 0.5,
              }}
            >
              {locationId}
            </Typography>
          )}
        </Box>
      </Stack>

      {/* Divider + description (max 2 lines, expand at end of 2nd line) */}
      <Box sx={{ borderTop: `1px solid ${COLORS.border}`, pt: 0.85 }}>
        {titleExpanded ? (
          <Typography sx={descriptionTextSx}>
            {fullTitle || "—"}
            {titleOverflows && (
              <Box
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  setTitleExpanded(false);
                }}
                sx={{ ...descriptionToggleSx, ml: 0.5 }}
              >
                collapse
              </Box>
            )}
          </Typography>
        ) : (
          <Box sx={{ position: "relative" }}>
            <Typography
              ref={descriptionRef}
              sx={{
                ...descriptionTextSx,
                display: "-webkit-box",
                WebkitLineClamp: DESCRIPTION_LINE_CLAMP,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                ...(titleOverflows && { pr: "3.25rem" }),
              }}
            >
              {fullTitle || "—"}
            </Typography>
            {titleOverflows && (
              <Box
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  setTitleExpanded(true);
                }}
                sx={{
                  ...descriptionToggleSx,
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  pl: 1.5,
                  fontSize: 12.5,
                  lineHeight: 1.4,
                  background: `linear-gradient(to right, transparent, ${COLORS.cardBg} 35%)`,
                }}
              >
                expand
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const BADGE_META = [
  { 
    key: "open", 
    label: "Open", 
    bg: "#fdecc8", 
    color: "#a5690a", 
    activeBg: COLORS.open, 
    activeColor: "#ffffff" 
  },
  { 
    key: "collaboration", 
    label: "Collab", 
    bg: "#e1f2fb", 
    color: "#1f7aa8", 
    activeBg: COLORS.collab, 
    activeColor: "#ffffff" 
  },
  { 
    key: "closed", 
    label: "Closed", 
    bg: "#e2f8ec", 
    color: "#1f9d57", 
    activeBg: COLORS.closed, 
    activeColor: "#ffffff" 
  },
];

const totalCount = (counts = {}) =>
  (counts.open || 0) + (counts.collaboration || 0) + (counts.closed || 0);

const MiniCountPills = ({ counts }) => (
  <Stack direction="row" spacing={0.4} alignItems="center" sx={{ flexShrink: 0 }}>
    {[
      { key: "open", color: COLORS.open },
      { key: "collaboration", color: COLORS.collab },
      { key: "closed", color: COLORS.closed },
    ].map((m) => (
      <Box
        key={m.key}
        sx={{
          minWidth: 18,
          height: 18,
          px: 0.5,
          borderRadius: "4px",
          bgcolor: m.color,
          color: "#fff",
          fontSize: 10,
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 1,
        }}
      >
        {counts?.[m.key] || 0}
      </Box>
    ))}
  </Stack>
);

/** Colored numbers only (no tile backgrounds) — for state & district rows. */
const StageCountText = ({ counts, fontSize = 11 }) => (
  <Stack direction="row" spacing={0.55} alignItems="baseline" sx={{ flexShrink: 0 }}>
    <Typography sx={{ fontSize, fontWeight: 700, color: "#c99400", lineHeight: 1 }}>
      {counts?.open || 0}
    </Typography>
    <Typography sx={{ fontSize: fontSize - 1, color: COLORS.border, fontWeight: 500 }}>|</Typography>
    <Typography sx={{ fontSize, fontWeight: 700, color: "#1f93cc", lineHeight: 1 }}>
      {counts?.collaboration || 0}
    </Typography>
    <Typography sx={{ fontSize: fontSize - 1, color: COLORS.border, fontWeight: 500 }}>|</Typography>
    <Typography sx={{ fontSize, fontWeight: 700, color: "#22a35a", lineHeight: 1 }}>
      {counts?.closed || 0}
    </Typography>
  </Stack>
);

const TreeChevron = ({ expanded }) =>
  expanded ? (
    <ExpandMoreIcon sx={{ fontSize: 18, color: COLORS.textMuted, flexShrink: 0 }} />
  ) : (
    <ChevronRightIcon sx={{ fontSize: 18, color: COLORS.textMuted, flexShrink: 0 }} />
  );

/**
 * Jira-like hierarchy nav: Plant > State > District.
 * Indentation + selection only — no overlapping connector lines.
 */
const HierarchySidebar = ({
  tree,
  expandedKeys,
  onToggle,
  selectedScope,
  onSelect,
  treeQuery,
  onTreeQueryChange,
  widthOverride,
  embedded = false,
}) => {
  const q = treeQuery.trim().toLowerCase();

  const selectedKeys = useMemo(() => {
    const keys = new Set();
    if (!selectedScope) return keys;
    keys.add(selectedScope.key);
    if (selectedScope.plant?.key) keys.add(selectedScope.plant.key);
    if (selectedScope.state?.key) keys.add(selectedScope.state.key);
    if (selectedScope.district?.key) keys.add(selectedScope.district.key);
    return keys;
  }, [selectedScope]);

  const visible = useMemo(() => {
    if (!q) return tree;
    return tree
      .map((plant) => {
        const plantHit = plant.name.toLowerCase().includes(q);
        const states = plant.states
          .map((state) => {
            const stateHit = state.name.toLowerCase().includes(q);
            const districts = state.districts.filter(
              (d) => stateHit || plantHit || d.name.toLowerCase().includes(q)
            );
            if (plantHit || stateHit || districts.length) {
              return {
                ...state,
                districts: plantHit || stateHit ? state.districts : districts,
              };
            }
            return null;
          })
          .filter(Boolean);
        return states.length ? { ...plant, states } : null;
      })
      .filter(Boolean);
  }, [tree, q]);

  const rowSx = (selected, depth) => ({
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    pl: 1 + depth * 1.5,
    pr: 1,
    py: 0.7,
    cursor: "pointer",
    borderRadius: "8px",
    borderLeft: selected ? `3px solid ${COLORS.navy}` : "3px solid transparent",
    bgcolor: selected ? "#D5E7FF" : "transparent",
    "&:hover": { bgcolor: selected ? "#D5E7FF" : "#f4f6f9" },
    userSelect: "none",
    minWidth: 0,
  });

  return (
    <Box
      sx={{
        width: widthOverride ?? SIDEBAR_WIDTH,
        flexShrink: 0,
        height: "100%",
        bgcolor: COLORS.cardBg,
        border: embedded ? "none" : `1px solid ${COLORS.border}`,
        borderRadius: embedded ? 0 : "12px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1, borderBottom: `1px solid ${COLORS.border}` }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.navy, mb: 0.25, textAlign:"left"}}>
          Hierarchy
        </Typography>
        <Typography sx={{ fontSize: 11, color: COLORS.textMuted, mb: 1,textAlign:"left"}}>
          Plant → State → District
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search plant, state, district…"
          value={treeQuery}
          onChange={(e) => onTreeQueryChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: COLORS.textMuted }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              fontSize: 12,
              height: 34,
              bgcolor: "#f7f8fb",
            },
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
        {visible.map((plant) => {
          const plantExpanded = expandedKeys.has(plant.key) || !!q;
          const plantSelected = selectedKeys.has(plant.key);
          return (
            <Box key={plant.key} sx={{ mb: 0.5 }}>
              <Box
                sx={rowSx(plantSelected, 0)}
                onClick={() => onSelect({ type: "plant", key: plant.key, plant })}
              >
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(plant.key);
                  }}
                  sx={{ display: "inline-flex", lineHeight: 0 }}
                >
                  <TreeChevron expanded={plantExpanded} />
                </Box>
                <Typography
                  noWrap
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 13,
                    fontWeight: 700,
                    color: COLORS.navy,
                    textAlign:"left"
                  }}
                >
                  {plant.name}
                </Typography>
                <MiniCountPills counts={plant.counts} />
              </Box>

              {plantExpanded &&
                plant.states.map((state) => {
                  const stateExpanded = expandedKeys.has(state.key) || !!q;
                  const stateSelected = selectedKeys.has(state.key);
                  return (
                    <Box key={state.key}>
                      <Box
                        sx={rowSx(stateSelected, 1)}
                        onClick={() =>
                          onSelect({ type: "state", key: state.key, plant, state })
                        }
                      >
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggle(state.key);
                          }}
                          sx={{ display: "inline-flex", lineHeight: 0 }}
                        >
                          <TreeChevron expanded={stateExpanded} />
                        </Box>
                        <Typography
                          noWrap
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: COLORS.textPrimary,
                            textAlign:"left"
                          }}
                        >
                          {state.name}
                        </Typography>
                        <StageCountText counts={state.counts} />
                      </Box>

                      {stateExpanded &&
                        state.districts.map((district) => {
                          const districtSelected = selectedKeys.has(district.key);
                          return (
                            <Box
                              key={district.key}
                              sx={rowSx(districtSelected, 2)}
                              onClick={() =>
                                onSelect({
                                  type: "district",
                                  key: district.key,
                                  plant,
                                  state,
                                  district,
                                })
                              }
                            >
                              <Box sx={{ width: 18, flexShrink: 0 }} />
                              <Typography
                                noWrap
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  fontSize: 12,
                                  fontWeight: districtSelected ? 600 : 500,
                                  color: COLORS.textPrimary,
                                  textAlign:"left"
                                }}
                              >
                                {district.name}
                              </Typography>
                              <StageCountText counts={district.counts} fontSize={10.5} />
                            </Box>
                          );
                        })}
                    </Box>
                  );
                })}
            </Box>
          );
        })}

        {!visible.length && (
          <Typography sx={{ fontSize: 12, color: COLORS.textMuted, textAlign: "center", py: 4 }}>
            No matching hierarchy.
          </Typography>
        )}
      </Box>
    </Box>
  );
};


const MobileScopeBar = ({ label, onOpen }) => (
  <Box
    onClick={onOpen}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      bgcolor: COLORS.cardBg,
      border: `1px solid ${COLORS.border}`,
      borderRadius: "10px",
      px: 1.5,
      py: 1,
      mb: 1.25,
      cursor: "pointer",
      flexShrink: 0,
      "&:active": { bgcolor: "#f4f6f9" },
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <TuneOutlinedIcon sx={{ fontSize: 17, color: COLORS.navy, flexShrink: 0 }} />
      <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700, color: COLORS.navy }}>
        {"Open hierarchy"}
      </Typography>
    </Stack>
    <ChevronRightIcon sx={{ fontSize: 20, color: COLORS.textMuted, flexShrink: 0 }} />
  </Box>
);

const StageColumn = ({ stage, issues, onIssueClick, refreshingIssueId, fixedHeight = true, isCompact = false }) => {
  const meta = STAGE_META[stage];
  return (
    <Box
      sx={{
        minWidth: 0,
        height: isCompact ? "100%" : (fixedHeight ? "100%" : "auto"),
        bgcolor: "#f4f6f9",
        borderRadius: "12px",
        border: `1px solid ${COLORS.border}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          pt: 1.25,
          pb: 1,
          borderBottom: `1px solid ${COLORS.border}`,
          borderTop: `3px solid ${meta.color}`,
          bgcolor: COLORS.cardBg,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: COLORS.textPrimary,
              textAlign:"left"
            }}
          >
            {meta.label}
          </Typography>
          <Chip
            label={issues.length}
            size="small"
            sx={{
              ...chipSx,
              height: 20,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: `${meta.color}33`,
              color: COLORS.textPrimary,
            }}
          />
        </Stack>
        <Typography sx={{ fontSize: 11, color: COLORS.textMuted, mt: 0.35, lineHeight: 1.3,textAlign:"left"}}>
          {meta.sub}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: fixedHeight ? 1 : "unset",
          overflowY: "auto",
          p: 1.25,
        }}
      >
        {issues.length ? (
          issues.map((issue) => (
            <IssueCard
              key={issue.id}
              issue={issue}
              stage={stage}
              onClick={onIssueClick}
              isRefreshing={String(refreshingIssueId) === String(issue.id)}
            />
          ))
        ) : (
          <Box
            sx={{
              py: 5,
              textAlign: "center",
              color: COLORS.textMuted,
              fontSize: 12,
              border: `1px dashed ${COLORS.border}`,
              borderRadius: "8px",
              bgcolor: "#fafbfd",
            }}
          >
            No issues
          </Box>
        )}
      </Box>
    </Box>
  );
};

const BoardKanban = ({
  breadcrumb,
  counts,
  issuesByStage,
  onIssueClick,
  refreshingIssueId,
  isCompact,
  activeStage,
  onStageChange,
}) => {
  const total = totalCount(counts);
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        height: isCompact ? "100%" : "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: COLORS.cardBg,
          border: `1px solid ${COLORS.border}`,
          borderRadius: "12px",
          px: 1.75,
          py: 0.75,
          display: "flex",
          alignItems: isCompact ? "flex-start" : "center",
          justifyContent: "space-between",
          gap: isCompact ? 1 : 1.5,
          flexWrap: "wrap",
          flexDirection: isCompact ? "column" : "row",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1, width: isCompact ? "100%" : "auto" }}>
          <Typography
            noWrap={!isCompact}
            sx={{ fontSize: isCompact ? 13 : 14, fontWeight: 700, color: COLORS.navy, lineHeight: 1.3,textAlign:"left" }}
          >
            {breadcrumb}{" "}
            <Box component="span" sx={{ fontWeight: 600, color: COLORS.navy, fontSize: 12 }}>
              ({total} issue{total === 1 ? "" : "s"})
            </Box>
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ flexWrap: "wrap", rowGap: 0.5, width: isCompact ? "100%" : "auto" }}
        >
          {BADGE_META.map((m) => {
            const active = isCompact && activeStage === m.key;
            return (
              <Chip
                key={m.key}
                label={`${m.label} ${counts?.[m.key] || 0}`}
                size="small"
                onClick={isCompact ? () => onStageChange(m.key) : undefined}
                sx={{
                  ...chipSx,
                  height: isCompact ? 26 : 20,
                  fontSize: isCompact ? 11.5 : 10.5,
                  fontWeight: 700,
                  bgcolor: active ? m.activeBg : m.bg,
                  color: active ? m.activeColor : m.color,
                  cursor: isCompact ? "pointer" : "default",
                  flex: isCompact ? 1 : "unset",
                  transition: "all 0.2s ease",
                  '&:hover': isCompact ? {
                    bgcolor: active ? m.activeBg : m.bg,
                    opacity: 0.9,
                  } : {},
                }}
              />
            );
          })}
        </Stack>
      </Box>
          
      {isCompact ? (
        <StageColumn
          stage={activeStage}
          issues={issuesByStage[activeStage] || []}
          onIssueClick={onIssueClick}
          refreshingIssueId={refreshingIssueId}
          fixedHeight={false}
          isCompact={true}
        />
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 1.5,
            minHeight: 0,
          }}
        >
          {STAGE_ORDER.map((stage) => (
            <StageColumn
              key={stage}
              stage={stage}
              issues={issuesByStage[stage] || []}
              onIssueClick={onIssueClick}
              refreshingIssueId={refreshingIssueId}
              fixedHeight
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const emptyStageIssues = () => ({ open: [], collaboration: [], closed: [] });
/*
const aggregateIssuesFromDistricts = (districts = []) => {
  const issues = emptyStageIssues();
  const counts = { open: 0, collaboration: 0, closed: 0 };
  districts.forEach((d) => {
    STAGE_ORDER.forEach((stage) => {
      const list = d.issues?.[stage] || [];
      issues[stage].push(...list);
      counts[stage] += list.length;
    });
  });
  return { issues, counts };
};*/
const aggregateIssuesFromDistricts = (districts = []) => {
  const issues = emptyStageIssues();
  const counts = { open: 0, collaboration: 0, closed: 0 };

  districts.forEach((d) => {
    STAGE_ORDER.forEach((stage) => {
      const list = d.issues?.[stage] || [];
      issues[stage].push(...list);
      counts[stage] += list.length;
    });
  });

  const sortByDateDesc = (list) =>
    list.sort((a, b) => b.date.localeCompare(a.date));

  STAGE_ORDER.forEach((stage) => {
    issues[stage] = sortByDateDesc(issues[stage]);
  });

  return { issues, counts };
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const LogisticsDashboard = () => {
  const STATE_PRIORITY = [
    "Rajasthan",
    "Gujarat",
    "Chhattisgarh",
    "Haryana",
  ];

  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  const [period, setPeriod] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All Status");

  const [month, setMonth] = useState(null);
  const [stateFilter, setStateFilter] = useState("All States");
  const [regionFilter, setRegionFilter] = useState("All Regions");
  const [districtFilter, setDistrictFilter] = useState("All Districts");
  const [escalation, setEscalation] = useState("All Escalations");
  const [plantFilter, setPlantFilter] = useState(ALL_PLANTS);
  const [keyword, setKeyword] = useState("");

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [filtersError, setFiltersError] = useState(null);

  const [dashboardData, setDashboardData] = useState([]);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [refreshingIssueId, setRefreshingIssueId] = useState(null);
  const [dashboardError, setDashboardError] = useState(null);

  // Expand/collapse + selection for Plant > State > District sidebar
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [selectedScope, setSelectedScope] = useState(null);
  const [treeQuery, setTreeQuery] = useState("");
  // Issue detail dialog state
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Compact-only UI state: which stage column is showing, and whether the
  // hierarchy Drawer is open.
  const [activeStage, setActiveStage] = useState("open");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const issueId = null;

  const handleIssueClick = (issue) => {
    setSelectedIssue(issue);
    setDetailDialogOpen(true);
  };

  const resetUrl = () => {};
  const handleCloseDetailDialog = (shouldRefresh = false) => {
    const issueIdToRefresh = shouldRefresh ? selectedIssue?.id : null;
    setDetailDialogOpen(false);
    resetUrl();
    if (shouldRefresh) {
      fetchDashboard({ silent: true, issueId: issueIdToRefresh });
    }
  };

  const sortStates = (states) => {
    return [...states].sort((a, b) => {
      const aIndex = STATE_PRIORITY.indexOf(a.name);
      const bIndex = STATE_PRIORITY.indexOf(b.name);

      const aPriority = aIndex !== -1;
      const bPriority = bIndex !== -1;

      // Both are on the priority list
      if (aPriority && bPriority) {
        return aIndex - bIndex;
      }

      // One is on the priority list
      if (aPriority) return -1;
      if (bPriority) return 1;

      // The rest are sorted alphabetically
      return a.name.localeCompare(b.name);
    });
  };

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleHierarchySelect = (payload) => {
    setSelectedScope(payload);
    // Auto-expand ancestors when selecting so the path stays visible
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (payload.plant?.key) next.add(payload.plant.key);
      if (payload.state?.key) next.add(payload.state.key);
      return next;
    });
    // On compact layouts, picking a location closes the drawer straight away
    if (isCompact) setMobileSidebarOpen(false);
  };

  const getMockRows = () => MOCK_RCA_RESPONSE?.row_data || [];

  const buildFiltersFromRows = (rows, { plant = "", state = "", region = "" } = {}) => {
    let filtered = rows;
    if (plant) filtered = filtered.filter((r) => (r.plant_description || "") === plant);
    if (state) filtered = filtered.filter((r) => (r.view_state || r.state || "") === state);
    if (region) filtered = filtered.filter((r) => (r.region || "") === region);

    const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort();
    const plants = uniq(rows.map((r) => r.plant_description)).map((v) => ({ label: v, value: v }));

    return {
      plant: plants,
      month: uniq(rows.map((r) => r.month)),
      state: uniq(filtered.map((r) => r.view_state || r.state)),
      region: uniq(filtered.map((r) => r.region)),
      district: uniq(filtered.map((r) => r.district)),
      escalation: uniq(filtered.map((r) => r.escalation)),
    };
  };

  const fetchFilters = async ({ plant = "", state = "", region = "" } = {}) => {
    setLoadingFilters(true);
    setFiltersError(null);
    try {
      const normalized = buildFiltersFromRows(getMockRows(), { plant, state, region });
      setFilters(normalized);
    } catch (err) {
      console.error("Failed to load RCA dashboard filters", err);
      setFiltersError("Couldn't load filters. Please try again.");
    } finally {
      setLoadingFilters(false);
    }
  };

  useEffect(() => {
    if (!filters.month.length) return;

    setMonth((prev) => prev ?? filters.month[0]);
  }, [filters.month]);

  useEffect(() => {
    fetchFilters({
      plant: plantFilter?.value === ALL_PLANTS.value ? "" : plantFilter?.value,
      month,
      state: stateFilter === "All States" ? "" : stateFilter,
      region: regionFilter === "All Regions" ? "" : regionFilter,
      district: districtFilter === "All Districts" ? "" : districtFilter,
      escalation: escalation === "All Escalations" ? "" : escalation,
    });
  }, [
    plantFilter?.value,
    stateFilter,
    regionFilter,
    districtFilter,
    escalation,
    month,
  ]);

  useEffect(() => {
    if (plantFilter?.value !== ALL_PLANTS.value && !filters.plant.some((p) => p.value === plantFilter?.value)) {
      setPlantFilter(ALL_PLANTS);
    }

    if (stateFilter !== "All States" && !filters.state.includes(stateFilter)) {
      setStateFilter("All States");
    }

    if (regionFilter !== "All Regions" && !filters.region.includes(regionFilter)) {
      setRegionFilter("All Regions");
    }

    if (districtFilter !== "All Districts" && !filters.district.includes(districtFilter)) {
      setDistrictFilter("All Districts");
    }

    if (escalation !== "All Escalations" && !filters.escalation.includes(escalation)) {
      setEscalation("All Escalations");
    }
  }, [filters]);

  // const sortByLatestDate = (issues) => issues.sort((a, b) => new Date(b.date) - new Date(a.date));
  const sortByLatestDate = (issues) => issues.sort((a, b) => b.date.localeCompare(a.date));
  // -------------------------------------------------------------------------
  // Fetch + build the Plant > State > District tree
  // -------------------------------------------------------------------------

  const fetchDashboard = async ({ silent = false, issueId = null } = {}) => {
    if (!month) return;

    if (silent && issueId) {
      setRefreshingIssueId(issueId);
    } else if (!silent) {
      setLoadingDashboard(true);
      setDashboardError(null);
    }

    try {
      let rows = getMockRows();

      if (period === "All" && month) {
        rows = rows.filter((r) => r.month === month);
      }
      if (stateFilter !== "All States") {
        rows = rows.filter((r) => (r.view_state || r.state) === stateFilter);
      }
      if (regionFilter !== "All Regions") {
        rows = rows.filter((r) => r.region === regionFilter);
      }
      if (districtFilter !== "All Districts") {
        rows = rows.filter((r) => r.district === districtFilter);
      }
      if (escalation !== "All Escalations") {
        rows = rows.filter((r) => r.escalation === escalation);
      }
      if (plantFilter?.value) {
        rows = rows.filter((r) => (r.plant_description || "") === plantFilter.value);
      }
      if (statusFilter !== "All Status") {
        rows = rows.filter((r) => (r.status || "").toLowerCase() === statusFilter.toLowerCase());
      }

      // plantKey -> { key, name, states: { stateKey -> { key, name, districts: { districtKey -> { key, name, issues } } } } }
      const plantsMap = {};

      rows.forEach((item) => {
        const plantName = item.plant_description?.trim() || UNASSIGNED_PLANT;
        const plantKey = `plant-${slug(plantName)}`;

        const stateName = item.view_state?.trim() || item.state?.trim() || UNKNOWN_STATE;
        const stateKey = `${plantKey}__state-${slug(stateName)}`;

        const districtName = item.district?.trim() || UNKNOWN_DISTRICT;
        const districtKey = `${stateKey}__district-${slug(districtName)}`;

        if (!plantsMap[plantKey]) {
          plantsMap[plantKey] = {
            key: plantKey,
            name: plantName,
            states: {},
          };
        }
        const plantNode = plantsMap[plantKey];

        if (!plantNode.states[stateKey]) {
          plantNode.states[stateKey] = {
            key: stateKey,
            name: stateName,
            districts: {},
          };
        }
        const stateNode = plantNode.states[stateKey];

        if (!stateNode.districts[districtKey]) {
          stateNode.districts[districtKey] = {
            key: districtKey,
            name: districtName,
            issues: {
              open: [],
              collaboration: [],
              closed: [],
            },
          };
        }
        const districtNode = stateNode.districts[districtKey];

        const issue = {
          id: item.id_issue,
          type: item.issue_category?.child_code || "SECONDARY",
          escalation: item.escalation || "",
          escalationCycle: item.escalation_cycle ?? item.cycle ?? null,

          site: item.plant_description || item.issue_category?.value || "-",

          title: item.root_cause,
          description: item.issue_description || item.description || item.root_cause || "",

          owner: item.responsible_name?.trim() || "Unassigned",
          responsibleName: item.responsible_name?.trim() || "",
          responsibleEmail: item.responsible_email || "",

          date: item.email_date || "",

          dealer: item.dealer_code,
          dealerName: item.dealer_name,

          updatedBy: item.updated_by_name,
          updatedByEmail: item.updated_by || "",
          lastCommentBy: item.last_comment_by || "",
          updatedAt: item.updated_at,
          status: item.status?.toLowerCase(),
          state: stateName,
          district: districtName,
          plantDepot: item.plant_description || "",
          category: item.issue_category?.value || "",
          categoryCode: item.issue_category?.category_code || "",
          subCategory: item.issue_category?.child_code || "",
          accountOwner: item.account_owner_email || item.account_owner || "",
          actionPlan: item.action_plan || "",
          activation_date: item.activation_date || "",
          forecast_shortfall: item.forecast_shortfall || "",
          actual: item.actual || 0,
          mtd_target: item.mtd_target || 0,
          achievement_pct: item.achievement_pct || "0%",
        };

        const statusKey = (item.status || "OPEN").toLowerCase();
        if (!districtNode.issues[statusKey]) {
          districtNode.issues[statusKey] = [];
        }
        districtNode.issues[statusKey].push(issue);
      });

      // const plantsArray = Object.values(plantsMap).map((plant) => ({
      //   ...plant,
      //   states: Object.values(plant.states).map((state) => ({
      //     ...state,
      //     districts: Object.values(state.districts),
      //   })),
      // }));
      
      const plantsArray = Object.values(plantsMap).map((plant) => ({
        ...plant,
        states: Object.values(plant.states).map((state) => ({
          ...state,
          districts: Object.values(state.districts).map((district) => ({
            ...district,
            issues: {
              open: sortByLatestDate(district.issues.open),
              collaboration: sortByLatestDate(district.issues.collaboration),
              closed: sortByLatestDate(district.issues.closed),
            },
          })),
        })),
      }));

      setDashboardData(plantsArray);
    } catch (err) {
      console.error(err);
      if (!silent) {
        setDashboardData([]);
        setDashboardError("Couldn't load dashboard.");
      }
    } finally {
      if (silent && issueId) {
        setRefreshingIssueId(null);
      } else if (!silent) {
        setLoadingDashboard(false);
      }
    }
  };

  useEffect(() => {
    if (!month && period === "ALL") return;

    fetchDashboard();
  }, [period, month, plantFilter?.value, stateFilter, regionFilter, districtFilter, escalation, statusFilter]);

  // Keyword search — filters issue cards within each district without
  // re-hitting the API, then drops empty districts/states/plants.
  const filteredDashboardData = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return dashboardData;

    const matches = (issue) => {
      const haystack = [
        issue.id,
        issue.title,
        issue.description,
        issue.dealer,
        issue.dealerName,
        issue.site,
        issue.owner,
        issue.type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    };

    return dashboardData
      .map((plant) => ({
        ...plant,
        states: plant.states
          .map((state) => ({
            ...state,
            districts: state.districts
              .map((district) => ({
                ...district,
                issues: {
                  open: district.issues.open.filter(matches),
                  collaboration: district.issues.collaboration.filter(matches),
                  closed: district.issues.closed.filter(matches),
                },
              }))
              .filter(
                (d) =>
                  d.issues.open.length ||
                  d.issues.collaboration.length ||
                  d.issues.closed.length
              ),
          }))
          .filter((s) => s.districts.length),
      }))
      .filter((p) => p.states.length);
  }, [dashboardData, keyword]);

  // Attach counts at every level and sort: plants alphabetically, states by
  // STATE_PRIORITY (then alphabetical), districts alphabetically.
  const visibleTree = useMemo(() => {
    const sumCounts = (list) =>
      list.reduce(
        (acc, c) => ({
          open: acc.open + c.open,
          collaboration: acc.collaboration + c.collaboration,
          closed: acc.closed + c.closed,
        }),
        { open: 0, collaboration: 0, closed: 0 }
      );

    const plants = filteredDashboardData.map((plant) => {
      const states = sortStates(plant.states).map((state) => {
        const districts = [...state.districts]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((d) => ({
            ...d,
            counts: {
              open: d.issues.open.length,
              collaboration: d.issues.collaboration.length,
              closed: d.issues.closed.length,
            },
          }));
        const counts = sumCounts(districts.map((d) => d.counts));
        return { ...state, districts, counts };
      });
      const counts = sumCounts(states.map((s) => s.counts));
      return { ...plant, states, counts };
    });

    return plants.sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredDashboardData]);

  // Keep selection valid when filters/search change; default to first plant.
  useEffect(() => {
    if (!visibleTree.length) {
      setSelectedScope(null);
      return;
    }

    setSelectedScope((prev) => {
      if (prev) {
        const stillValid =
          (prev.type === "plant" && visibleTree.some((p) => p.key === prev.key)) ||
          (prev.type === "state" &&
            visibleTree.some((p) => p.states.some((s) => s.key === prev.key))) ||
          (prev.type === "district" &&
            visibleTree.some((p) =>
              p.states.some((s) => s.districts.some((d) => d.key === prev.key))
            ));
        if (stillValid) return prev;
      }

      const firstPlant = visibleTree[0];
      setExpandedKeys((keys) => new Set([...keys, firstPlant.key]));
      return { type: "plant", key: firstPlant.key, plant: firstPlant };
    });
  }, [visibleTree]);
  const boardView = useMemo(() => {
    if (!selectedScope) {
      return {
        scopeLabel: "Select a location",
        breadcrumb: "Select a location",
        counts: { open: 0, collaboration: 0, closed: 0 },
        issuesByStage: emptyStageIssues(),
      };
    }

    if (selectedScope.type === "district" && selectedScope.district) {
      const d = selectedScope.district;
      // Re-resolve from visibleTree so counts stay fresh after filter/search
      let live = d;
      let livePlant = selectedScope.plant;
      let liveState = selectedScope.state;
      for (const p of visibleTree) {
        for (const s of p.states) {
          const found = s.districts.find((x) => x.key === selectedScope.key);
          if (found) {
            live = found;
            livePlant = p;
            liveState = s;
          }
        }
      }
      const parts = [livePlant?.name, liveState?.name, live.name].filter(Boolean);
      return {
        scopeLabel: live.name,
        breadcrumb: parts.join(" > "),
        counts: live.counts || {
          open: live.issues.open.length,
          collaboration: live.issues.collaboration.length,
          closed: live.issues.closed.length,
        },
        issuesByStage: {
          open: live.issues.open,
          collaboration: live.issues.collaboration,
          closed: live.issues.closed,
        },
      };
    }

    if (selectedScope.type === "state") {
      let liveState = selectedScope.state;
      let livePlant = selectedScope.plant;
      for (const p of visibleTree) {
        const found = p.states.find((s) => s.key === selectedScope.key);
        if (found) {
          liveState = found;
          livePlant = p;
        }
      }
      const { issues, counts } = aggregateIssuesFromDistricts(liveState?.districts || []);
      const parts = [livePlant?.name, liveState?.name].filter(Boolean);
      return {
        scopeLabel: liveState?.name || "",
        breadcrumb: parts.join(" > "),
        counts,
        issuesByStage: issues,
      };
    }

    // plant
    let livePlant = selectedScope.plant;
    const foundPlant = visibleTree.find((p) => p.key === selectedScope.key);
    if (foundPlant) livePlant = foundPlant;
    const districts = (livePlant?.states || []).flatMap((s) => s.districts);
    const { issues, counts } = aggregateIssuesFromDistricts(districts);
    return {
      scopeLabel: livePlant?.name || "",
      breadcrumb: livePlant?.name || "",
      counts,
      issuesByStage: issues,
    };
  }, [selectedScope, visibleTree]);

  // Flat list of every visible issue, used for the summary banner totals.
  const allIssuesFlat = useMemo(() => {
    const arr = [];
    filteredDashboardData.forEach((plant) => {
      plant.states.forEach((state) => {
        state.districts.forEach((district) => {
          arr.push(
            ...district.issues.open,
            ...district.issues.collaboration,
            ...district.issues.closed
          );
        });
      });
    });
    return arr;
  }, [filteredDashboardData]);

  const stateCount = useMemo(() => {
    const set = new Set();
    filteredDashboardData.forEach((plant) =>
      plant.states.forEach((s) => set.add(s.name))
    );
    return set.size;
  }, [filteredDashboardData]);

  const findIssue = (targetIssueId) => {
    for (const plant of dashboardData) {
      for (const state of plant.states) {
        for (const district of state.districts) {
          const all = [
            ...district.issues.open,
            ...district.issues.collaboration,
            ...district.issues.closed,
          ];
          const found = all.find((issue) => String(issue.id) === String(targetIssueId));
          if (found) return found;
        }
      }
    }
    return null;
  };

  useEffect(() => {
    if (!issueId) return;
    if (!dashboardData.length) return;

    const issue = findIssue(issueId);

    if (!issue) {
      resetUrl();
      return;
    } {
      setSelectedIssue(issue);
      setDetailDialogOpen(true);
    }
  }, [dashboardData, issueId]);

  const totals = useMemo(() => {
    return allIssuesFlat.reduce(
      (acc, issue) => {
        if (issue.status === "open") acc.open += 1;
        else if (issue.status === "collaboration") acc.collaboration += 1;
        else if (issue.status === "closed") acc.closed += 1;
        return acc;
      },
      { open: 0, collaboration: 0, closed: 0 }
    );
  }, [allIssuesFlat]);

  return (
    <Box
      className="logistics-rca-dashboard"
      sx={{ borderRadius: "16px", mt: { xs: 1.5, md: 3 }, px: { xs: 1, sm: 1.5, md: 0 } }}
    >
      {/* Isolate from global .text-center / table header text-align leaks after visiting other modules */}
      <style>{`
        .logistics-rca-dashboard .logistics-rca-issue-card,
        .logistics-rca-dashboard .logistics-rca-issue-card .MuiTypography-root {
          text-align: left !important;
        }
      `}</style>
      <KeywordSearch value={keyword} onChange={setKeyword} isCompact={isCompact} />

      {/* Filters + summary — scroll with the page */}
      <Box sx={{ pt: 0.5, pb: 1, mb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 1.25,
            flexWrap: "wrap",
            rowGap: 1.25,
          }}
        >
          <FilterDropdown
            label="Period"
            value={period}
            options={["All", "Last 3 Months"]}
            onChange={setPeriod}
            disableClearable
            width={140}
            isCompact={isCompact}
          />
          <FilterDropdown width={120} label="Month" value={month} options={filters.month} onChange={setMonth} disabled={period !== "All"} isCompact={isCompact} />
          <FilterDropdown
            label="Plant"
            width={170}
            value={plantFilter}
            options={[ALL_PLANTS, ...filters.plant]}
            onChange={setPlantFilter}
            disableClearable
            isCompact={isCompact}
          />
          <FilterDropdown
            label="State"
            value={stateFilter}
            width={180}
            options={["All States", ...filters.state]}
            onChange={setStateFilter}
            disableClearable
            isCompact={isCompact}
          />
          <FilterDropdown
            label="Region"
            width={180}
            value={regionFilter}
            options={["All Regions", ...filters.region]}
            onChange={setRegionFilter}
            disableClearable
            isCompact={isCompact}
          />
          <FilterDropdown
            label="District"
            width={150}
            value={districtFilter}
            options={["All Districts", ...filters.district]}
            onChange={setDistrictFilter}
            disableClearable
            isCompact={isCompact}
          />
          <FilterDropdown width={150} label="Escalation" value={escalation} options={["All Escalations", ...filters.escalation]} onChange={setEscalation} isCompact={isCompact} />
          <FilterDropdown
            label="Status"
            value={statusFilter}
            width={130}
            options={["All Status", "Open", "Collaboration", "Closed"]}
            onChange={setStatusFilter}
            disableClearable
            isCompact={isCompact}
          />
        </Box>

        {filtersError && (
          <Typography sx={{ fontSize: 12.5, color: COLORS.e3Text, mb: 1 }}>
            {filtersError}
          </Typography>
        )}

        {!loadingDashboard && !dashboardError && (
          <SummaryBanner stateCount={stateCount} totals={totals} />
        )}
      </Box>

      {loadingDashboard ? (
        <Box sx={{ textAlign: "center" }}>
          <DashboardSkeleton isCompact={isCompact} />
        </Box>
      ) : dashboardError ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography color="error">{dashboardError}</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            mb: 3,
            position: "sticky",
            top: isCompact ? 0 : 64,
            zIndex: 8,
            height: isCompact ? "calc(100vh - 100px)" : "calc(100vh - 168px)",
            maxHeight: isCompact ? "calc(100vh - 100px)" : "calc(100vh - 168px)",
            bgcolor: isCompact ? COLORS.pageBg : "#f4f6f9",
            overflow: "hidden",
            pt: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: isCompact ? "column" : "row",
              gap: 2,
              alignItems: "stretch",
              height: "100%",
              overflow: "hidden",
              pt: 0.5,
            }}
          >
            {isCompact ? (
              <>
                <MobileScopeBar label={boardView.breadcrumb} onOpen={() => setMobileSidebarOpen(true)} />
                <Drawer
                  anchor="left"
                  open={mobileSidebarOpen}
                  onClose={() => setMobileSidebarOpen(false)}
                  PaperProps={{
                    sx: {
                      width: "86%",
                      maxWidth: 340,
                    },
                  }}
                >
                  <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1.5,
                        py: 1.1,
                        borderBottom: `1px solid ${COLORS.border}`,
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>
                        Browse hierarchy
                      </Typography>
                      <IconButton size="small" onClick={() => setMobileSidebarOpen(false)}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                      <HierarchySidebar
                        tree={visibleTree}
                        expandedKeys={expandedKeys}
                        onToggle={toggleExpand}
                        selectedScope={selectedScope}
                        onSelect={handleHierarchySelect}
                        treeQuery={treeQuery}
                        onTreeQueryChange={setTreeQuery}
                        widthOverride="100%"
                        embedded
                      />
                    </Box>
                  </Box>
                </Drawer>
              </>
            ) : (
              <HierarchySidebar
                tree={visibleTree}
                expandedKeys={expandedKeys}
                onToggle={toggleExpand}
                selectedScope={selectedScope}
                onSelect={handleHierarchySelect}
                treeQuery={treeQuery}
                onTreeQueryChange={setTreeQuery}
              />
            )}

            {visibleTree.length ? (
              <BoardKanban
                breadcrumb={boardView.breadcrumb}
                counts={boardView.counts}
                issuesByStage={boardView.issuesByStage}
                onIssueClick={handleIssueClick}
                refreshingIssueId={refreshingIssueId}
                isCompact={isCompact}
                activeStage={activeStage}
                onStageChange={setActiveStage}
              />
            ) : (
              <Box
                sx={{
                  flex: 1,
                  py: 8,
                  textAlign: "center",
                  bgcolor: COLORS.cardBg,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: "12px",
                }}
              >
                <Typography sx={{ fontSize: 13, color: COLORS.textMuted }}>
                  {keyword
                    ? `No issues match "${keyword}".`
                    : "No issues for the selected filters."}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <IssueDetailDialog
        issue={selectedIssue}
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
      />
    </Box>
  );
};

export default LogisticsDashboard;